import { fetchUtils } from '$utils/fetchUtils'
import { get } from 'svelte/store'
import { t } from '$i18n/i18n'
import { NotificationType } from '$types/notification'
import { addNotification } from '../stores'
import type { PubPageParams } from './types/pubPageParams'
import { EmptyOffer, type Offer, type Offers, OfferState } from './types/offer'
import { RequestType } from './types/requestDto'
import { LotType } from '$types/enums'

export async function loadOfferAttachments(offers: Offers, urlParams: PubPageParams | null) {
	const offerPublicId =
		offers?.material?.publicId ||
		offers?.materialWithTransport?.publicId ||
		offers?.transport?.publicId
	const result: any[] = []
	if (!offerPublicId || (typeof offerPublicId === 'number' && offerPublicId <= 0)) {
		if (urlParams == null) {
			return result
		}
		const {
			requestPublicId,
			requestedEntityType,
			requestedEntityId,
			offerUserPublicId,
			lotPublicId
		} = urlParams
		let url: string
		if (
			lotPublicId &&
			!requestPublicId &&
			!requestedEntityType &&
			!requestedEntityId &&
			!offerUserPublicId
		) {
			url = `/api/optimizer/lot/${lotPublicId}/attachments`
		} else {
			url = `/api/optimizer/request/${requestPublicId}/requested/${requestedEntityType}/${requestedEntityId}/user/${offerUserPublicId}/attachments`
		}
		console.error('attachments', 'url', url)
		await fetchUtils
			.get(url)
			.then((data) => {
				console.error('attachments', 'data', data)
				Object.assign(result, data)
			})
			.catch((error) => console.error(error))
	} else {
		// at the moment, we have only one field to upload attachments, and as we duplicate the attachments to both
		// offers, we don't need to load them for both (at the moment!!)
		await fetchUtils
			.get(`/api/offer/${offerPublicId}/attachments`)
			.then((data) => {
				Object.assign(result, data)
			})
			.catch((error) => console.error(error))
	}

	return result
}

function savePublicOptimizerComment(optimizerPublicComment: string, offerId: string): void {
	const request = { optimizerPublicComment }

	fetchUtils
		.post(`/api/optimizer/offer/${offerId}/updateOptimizerPublicComment`, request)
		.then((_) => {
			const notification = {
				message: get(t)('UI.notification.saved'),
				type: NotificationType.SUCCESS,
				timeout: 4000,
				dismissible: false
			}
			addNotification(notification)
		})
		.catch((_error) => {
			const notification = {
				message: get(t)('UI.error.save'),
				type: NotificationType.ERROR,
				timeout: 0,
				dismissible: true
			}
			addNotification(notification)
		})
}

export function anyOfferAlreadyAccepted(offers: Offers) {
	if (!offers) {
		return false
	}
	return Object.values(offers).some((offer) => offer?.offerState === OfferState.ACCEPTED)
}

export function saveOptimizerPublicComment(offers: Offers) {
	if (!offers) {
		return
	}
	let acceptedOffer = Object.values(offers).find(
		(offer) => offer.offerState === OfferState.ACCEPTED
	)
	if (!acceptedOffer) {
		acceptedOffer = Object.values(offers).find(
			(offer) =>
				offer.optimizerPublicComment !== null &&
				offer.optimizerPublicComment !== undefined &&
				offer.optimizerPublicComment !== ''
		)
	}
	if (!acceptedOffer) {
		return
	}
	savePublicOptimizerComment(acceptedOffer.optimizerPublicComment, acceptedOffer.publicId)
}

export function checkEmptyOffers(offers: Offers) {
	// Return true if offers is null/undefined or if all offers are null/empty
	if (
		!offers ||
		Object.values(offers).every((offer) => !offer || isEmptyOffer(offer, offer.requestType))
	) {
		return true
	}

	// Get valid offers and separate them by type
	const validOffers: Offer[] = Object.values(offers).filter((offer) => offer)
	const transportOffers: Offer[] = validOffers.filter(
		(offer) => offer.requestType === RequestType.TRANSPORT
	)
	const otherOffers: Offer[] = validOffers.filter(
		(offer) => offer.requestType !== RequestType.TRANSPORT
	)

	// Return true if all transport offers are in DRAFT state (if any exist)
	// OR all other offers are in DRAFT state
	return (
		transportOffers.every((offer) => offer.offerState === OfferState.DRAFT) ||
		otherOffers.every((offer) => offer.offerState === OfferState.DRAFT)
	)
}

export function getInfoText(offerText, requestType: RequestType) {
	switch (requestType) {
		case RequestType.MATERIAL:
			return get(t)('UI.Offer.exclusiveTransp.desc', { offerText })
		case RequestType.MATERIAL_WITH_TRANSPORT:
			return get(t)('UI.Offer.inclusiveTransp.desc', { offerText })
		case RequestType.TRANSPORT:
			return get(t)('UI.Offer.transport.desc', { offerText })
	}
}

export function isEmptyOffer(offer: Offer, requestType: RequestType) {
	return JSON.stringify(offer) === JSON.stringify(EmptyOffer(requestType))
}

export function isOfferProvidedAndOtherOfferNotAccepted(
	offer: Offer,
	requestType: RequestType = RequestType.MATERIAL,
	otherOffer: Offer,
	selectable: boolean,
	lotType: LotType
) {
	if (lotType === LotType.TRANSPORT) {
		return (
			offer &&
			!isEmptyOffer(offer, requestType) &&
			((selectable &&
				(offer.offerState === OfferState.READY || offer.offerState === OfferState.SENT)) ||
				!selectable)
		)
	}
	return (
		(offer &&
			!isEmptyOffer(offer, requestType) &&
			((selectable &&
				((offer.offerState === OfferState.READY &&
					(!otherOffer?.offerState || otherOffer?.offerState === OfferState.READY)) ||
					offer.offerState === OfferState.SENT)) ||
				!selectable)) ||
		false
	)
}

export function isAnyOfferNotEmpty(offers: Offers) {
	if (!offers) {
		return false
	}
	return Object.values(offers).some((offer) => !isEmptyOffer(offer, offer.requestType))
}

export function isAnyOfferAccepted(offers: Offers) {
	if (!offers) {
		return false
	}
	return Object.values(offers).some((o) => o.offerState === OfferState.ACCEPTED)
}

export function isAnyOfferOutdated(offers: Offers, lotVolume: number) {
	if (!offers) {
		return false
	}
	return Object.values(offers).some(
		(offer) => offer?.offerState === OfferState.SENT && (offer?.facts?.volume || 0) > lotVolume
	)
}

export function checkIsAnyOfferRejected(offers: Offers) {
	if (!offers) {
		return false
	}
	return Object.values(offers).some(
		(offer) =>
			offer?.offerState &&
			(offer?.offerState === OfferState.REJECTED || offer?.offerState === OfferState.REJECTED_MM)
	)
}

export function checkAreAllOffersRejected(offers: Offers) {
	if (!offers) {
		return false
	}
	return Object.values(offers).every(
		(offer) => offer?.offerState && offer.offerState === OfferState.REJECTED
	)
}

export function areAllRejectedAndNotAllEmpty(offers: Offers) {
	if (!offers) {
		return false
	}
	return (
		Object.values(offers).every(
			(offer) =>
				isEmptyOffer(offer, offer.requestType) ||
				offer.offerState === OfferState.REJECTED ||
				offer.offerState === OfferState.REJECTED_MM
		) && !Object.values(offers).every((offer) => isEmptyOffer(offer, offer?.requestType))
	)
}

export function isOfferRejected(offer: Offer) {
	return (
		!isEmptyOffer(offer, offer.requestType) &&
		(offer.offerState === OfferState.REJECTED || offer.offerState === OfferState.REJECTED_MM)
	)
}

export function isOfferRejectedByOptimizer(offer: Offer) {
	return !isEmptyOffer(offer, offer.requestType) && offer.offerState == OfferState.REJECTED
}
